import { getEventLabel, useEntityLogs } from '@/hooks/useEntityLogs';
import { getAvatarColor } from '@/lib/colors';
import { mdiAccountCircle, mdiClockOutline, mdiRobot } from '@mdi/js';
import * as React from 'react';
import { Icon } from './ui/Icon';
import { Pagination } from './ui/Pagination';

export interface EntityLogsTabProps {
  entityType: 'business' | 'user' | 'import_job' | 'review' | 'plan';
  entityId: number | undefined;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatProperties = (properties: Record<string, unknown>): string => {
  if (!properties || Object.keys(properties).length === 0) {
    return '';
  }

  const parts: string[] = [];

  // Handle specific property types
  if (properties.changedFields && Array.isArray(properties.changedFields)) {
    parts.push(`Changed: ${(properties.changedFields as string[]).join(', ')}`);
  }
  if (properties.appliedChanges && Array.isArray(properties.appliedChanges)) {
    parts.push(
      `Applied: ${(properties.appliedChanges as string[]).join(', ')}`,
    );
  }
  if (properties.status) {
    parts.push(`Status: ${properties.status}`);
  }
  if (properties.enabled !== undefined) {
    parts.push(`Enabled: ${properties.enabled ? 'Yes' : 'No'}`);
  }
  if (properties.role) {
    parts.push(`Role: ${properties.role}`);
  }
  if (properties.provider) {
    parts.push(`Provider: ${properties.provider}`);
  }
  if (properties.itemCount !== undefined) {
    parts.push(`Items: ${properties.itemCount}`);
  }
  if (properties.totalItems !== undefined) {
    parts.push(`Total: ${properties.totalItems}`);
  }
  if (properties.successfulItems !== undefined) {
    parts.push(`Success: ${properties.successfulItems}`);
  }
  if (properties.failedItems !== undefined && properties.failedItems !== 0) {
    parts.push(`Failed: ${properties.failedItems}`);
  }
  if (properties.skippedItems !== undefined && properties.skippedItems !== 0) {
    parts.push(`Skipped: ${properties.skippedItems}`);
  }
  if (properties.score !== undefined) {
    parts.push(`Score: ${properties.score}`);
  }
  if (properties.reviewId !== undefined) {
    parts.push(`Review #${properties.reviewId}`);
  }
  if (properties.isEvaluated !== undefined) {
    parts.push(`Visible: ${properties.isEvaluated ? 'Yes' : 'No'}`);
  }
  if (properties.name) {
    parts.push(`Name: ${properties.name}`);
  }

  return parts.join(' | ');
};

export const EntityLogsTab: React.FC<EntityLogsTabProps> = ({
  entityType,
  entityId,
}) => {
  const [page, setPage] = React.useState(1);
  const [showViews, setShowViews] = React.useState(false);
  const limit = 15;

  const { data, isLoading } = useEntityLogs(entityType, entityId, {
    page,
    limit,
    excludeViews: !showViews,
  });

  const hasLogs = data?.logs && data.logs.length > 0;

  return (
    <div className='flex flex-col h-full'>
      {/* Filter toggle - always visible */}
      <div className='px-4 py-3 border-b border-grey-200 flex items-center justify-between'>
        <label className='flex items-center gap-2 cursor-pointer'>
          <input
            type='checkbox'
            checked={showViews}
            onChange={e => {
              setShowViews(e.target.checked);
              setPage(1);
            }}
            className='w-4 h-4 text-coffice-blue rounded border-grey-300 focus:ring-coffice-blue'
          />
          <span className='text-sm text-grey-600'>Show view events</span>
        </label>
        <span className='text-xs text-grey-400'>
          {isLoading
            ? '...'
            : `${data?.total || 0} ${data?.total === 1 ? 'event' : 'events'}`}
        </span>
      </div>

      {isLoading ? (
        <div className='flex-1 flex items-center justify-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-coffice-blue'></div>
        </div>
      ) : !hasLogs ? (
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center py-12'>
            <Icon
              path={mdiClockOutline}
              size={48}
              className='mx-auto text-grey-300 mb-4'
            />
            <h3 className='text-lg font-medium text-grey-900'>
              No activity yet
            </h3>
            <p className='text-grey-500 mt-1'>
              Activity logs will appear here when changes are made.
            </p>
          </div>
        </div>
      ) : (
        <div className='flex-1 overflow-y-auto'>
          <div className='divide-y divide-grey-100'>
            {data.logs.map(log => {
              const avatarColor = log.user
                ? getAvatarColor(log.user.id)
                : { bg: 'bg-grey-100', text: 'text-grey-500' };

              return (
                <div key={log.id} className='p-4 hover:bg-grey-50'>
                  <div className='flex items-start gap-3'>
                    {/* User Avatar or System Icon */}
                    <div className='flex-shrink-0'>
                      {log.user ? (
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${avatarColor.bg}`}
                        >
                          <Icon
                            path={mdiAccountCircle}
                            size={20}
                            className={avatarColor.text}
                          />
                        </div>
                      ) : (
                        <div className='w-8 h-8 bg-grey-100 rounded-full flex items-center justify-center'>
                          <Icon
                            path={mdiRobot}
                            size={20}
                            className='text-grey-500'
                          />
                        </div>
                      )}
                    </div>

                    {/* Event Details */}
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <span className='font-medium text-grey-900'>
                          {getEventLabel(log.eventType)}
                        </span>
                        {log.user && (
                          <span className='text-sm text-grey-500'>
                            by{' '}
                            {log.user.firstName || log.user.lastName
                              ? `${log.user.firstName || ''} ${log.user.lastName || ''}`.trim()
                              : 'Unknown'}
                          </span>
                        )}
                        {!log.user && (
                          <span className='text-sm text-grey-400'>
                            by System
                          </span>
                        )}
                      </div>

                      {/* Properties */}
                      {log.properties &&
                        Object.keys(log.properties).length > 0 && (
                          <p className='text-sm text-grey-600 mt-1'>
                            {formatProperties(log.properties)}
                          </p>
                        )}

                      {/* Timestamp */}
                      <p className='text-xs text-grey-400 mt-1'>
                        {formatDate(log.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pagination */}
      {hasLogs && data.totalPages > 1 && (
        <div className='border-t border-grey-200 p-4'>
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};
